import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Plus, Edit, Trash2, Users, Copy, RefreshCw, Calendar, DollarSign, Activity, TrendingUp, Zap, Filter } from 'lucide-react';
import { FormField, Button, Modal, Drawer, Card, Badge, DataTable, Spinner, Divider, FormDatePicker } from '../ui';
import { PasswordField } from '../ui/forms';
import { useToastContext } from '../ui/feedback/ToastProvider';
import DirhamSymbol from '../components/DirhamSymbol';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { sanitizeFormData } from '../utils/sanitize';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';

const Brokers = () => {
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBroker, setEditingBroker] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [brokerInvestors, setBrokerInvestors] = useState([]);
  const [loadingInvestors, setLoadingInvestors] = useState(false);
  const [timeseries, setTimeseries] = useState([]);
  const [timeseriesLoading, setTimeseriesLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('30');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [selectedBrokerIds, setSelectedBrokerIds] = useState([]);
  // Live minute-level commission per broker
  const [brokerLiveData, setBrokerLiveData] = useState({}); // brokerId -> { perMinAED, perDayAED, avgROI }
  const [liveLoading, setLiveLoading] = useState(false);
  const [autoRefreshLive, setAutoRefreshLive] = useState(true);
  const liveRefreshRef = React.useRef(null);
  const [checkedBrokers, setCheckedBrokers] = useState(new Set());
  const [allocatedProfitsPeriod, setAllocatedProfitsPeriod] = useState('30');
  const [brokerAllocatedProfits, setBrokerAllocatedProfits] = useState({
    direct_allocations: [],
    commission_distributed: [],
    summary: {
      total_direct_allocated: 0,
      total_distributed_aed: 0,
      total_distributed_usdt: 0
    }
  });
    const [showBreakdownModal, setShowBreakdownModal] = useState(false);
    const [breakdownData, setBreakdownData] = useState(null);
    const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  
  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyBroker, setHistoryBroker] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    startDate: null,
    endDate: null
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    active: true,
    startDate: null
  });

  useEffect(() => {
    fetchBrokers();
  }, []);

  useEffect(() => {
    if (selectedBrokerIds.length === 0 && brokers.length > 0) {
      setSelectedBrokerIds(['ALL']);
    }
  }, [brokers, selectedBrokerIds.length]);

  useEffect(() => {
    if (brokers.length === 0) return;
    fetchTimeseries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, useCustomRange, customRange.from, customRange.to, selectedBrokerIds]);

  // Auto-refresh live commission per broker every 30s
  useEffect(() => {
    if (!autoRefreshLive || brokers.length === 0) return;
    const run = () => refreshAllBrokerLive();
    run();
    liveRefreshRef.current = setInterval(run, 30000);
    return () => {
      if (liveRefreshRef.current) clearInterval(liveRefreshRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshLive, brokers.length]);

  const refreshAllBrokerLive = async () => {
    try {
      setLiveLoading(true);
      const dataMap = {};

      // Fetch profit allocation totals for all brokers
      let profitAllocationArray = [];
      try {
        const allocationResponse = await api.get('/api/profit-allocations/broker-totals');
        const allocationData = allocationResponse.data.brokerTotals || {};
        
        // Check if it's an array or object
        if (Array.isArray(allocationData)) {
          profitAllocationArray = allocationData;
        } else {
          // Convert object to array
          profitAllocationArray = Object.keys(allocationData).map(broker_id => ({
            broker_id,
            total_aed: allocationData[broker_id].total_aed || 0,
            pending_balance: allocationData[broker_id].pending_balance || 0
          }));
        }
        
        console.log('[BrokerLive] Profit allocation array received:', profitAllocationArray);
      } catch (err) {
        console.error('[BrokerLive] Error fetching profit allocation totals:', err);
      }

      // Fetch payout totals for all brokers
      let payoutArray = [];
      try {
        const payoutResponse = await api.get('/api/brokers/payout-totals');
        payoutArray = payoutResponse.data.payoutTotals || [];
        
        console.log('[BrokerLive] Payout API response:', payoutResponse.data);
        console.log('[BrokerLive] Payout array:', payoutArray);
        console.log('[BrokerLive] Payout array length:', payoutArray.length);
        if (payoutArray.length > 0) {
          console.log('[BrokerLive] First payout item:', payoutArray[0]);
        }
        console.log('[BrokerLive] First broker ID from list:', brokers[0]?._id);
        console.log('[BrokerLive] First broker ID type:', typeof brokers[0]?._id);
      } catch (err) {
        console.error('[BrokerLive] Error fetching payout totals:', err);
      }

      for (const broker of brokers) {
        try {
          console.log(`[BrokerLive] Processing broker ${broker.name} (${broker._id})`);
          
          const brokerIdStr = String(broker._id);
          
          // Use data already fetched from /api/brokers which includes BrokerDailyCommission aggregations
          const totalAED = broker.total_profit || 0; // All-time total from BrokerDailyCommission
          const todayAED = broker.today_daily_commission || 0; // Today's commission from BrokerDailyCommission
          const monthlyAED = broker.mtd_daily_commission || 0; // Month-to-date from BrokerDailyCommission
          
          // Use aggregated values from broker_daily_return in DailyROI
          const aggregatedBalanceAED = broker.aggregated_balance_aed || 0; // Aggregated broker_daily_return (all-time)
          const aggregatedMonthlyAED = broker.aggregated_monthly_aed || 0; // Aggregated broker_daily_return (MTD)
          
          // Get ProfitAllocation totals for this broker
          const profitAllocation = profitAllocationArray.find(item => 
            String(item.broker_id) === brokerIdStr
          );
          const profitAllocationAED = profitAllocation?.total_aed || 0;
          const profitAllocationPending = profitAllocation?.pending_balance || 0;
          
          // Get total payouts for this broker - search in flat array
          const payoutItem = payoutArray.find(item => item?.total_payout);
          const totalPayoutsAED = payoutItem?.total_payout || 0;
          
          console.log(`[BrokerLive] Broker ${broker.name} (${brokerIdStr}) payout lookup:`, {
            foundPayoutItem: payoutItem,
            totalPayoutsAED: totalPayoutsAED,
            payoutArrayLength: payoutArray.length,
            searchingForId: brokerIdStr
          });
          
          // Combined balance: aggregated DailyROI balance + ProfitAllocation balance - Payouts
          const combinedBalanceAED = aggregatedBalanceAED + profitAllocationAED - totalPayoutsAED;
          
          console.log(`[BrokerLive] Broker ${broker.name} final balance:`, {
            aggregatedBalanceAED,
            profitAllocationAED,
            totalPayoutsAED,
            combinedBalanceAED: combinedBalanceAED
          });
          
          // Average ROI is now calculated from BrokerInvestor relationships (will be added by backend in future)
          // For now, use investor average ROI as a proxy
          const avgROI = broker.today_roi_percentage || 0;
          
          // Calculate per minute/day based on actual daily commission
          const today = new Date();
          const daysInMonth = today.getDate();
          const perDayAED = daysInMonth > 0 ? (monthlyAED / daysInMonth) : todayAED;
          const perMinAED = perDayAED / (24 * 60);

          console.log(`[BrokerLive] Broker ${broker.name} values:`, {
            totalAED,
            todayAED,
            monthlyAED,
            avgROI,
            perMinAED,
            perDayAED,
            aggregatedBalanceAED,
            aggregatedMonthlyAED,
            profitAllocationAED,
            profitAllocationPending,
            totalPayoutsAED,
            combinedBalanceAED
          });

          dataMap[broker._id] = {
            perMinAED,
            perDayAED,
            avgROI,
            totalAED,
            todayAED,
            monthlyAED,
            aggregatedBalanceAED,
            aggregatedMonthlyAED,
            profitAllocationAED,
            profitAllocationPending,
            totalPayoutsAED,
            combinedBalanceAED
          };
        } catch (err) {
          console.error(`[BrokerLive] Error processing broker ${broker.name}:`, err.message);
          dataMap[broker._id] = { 
            perMinAED: 0, 
            perDayAED: 0, 
            avgROI: 0, 
            totalAED: 0,
            todayAED: 0,
            monthlyAED: 0,
            aggregatedBalanceAED: 0,
            aggregatedMonthlyAED: 0,
            profitAllocationAED: 0,
            profitAllocationPending: 0,
            totalPayoutsAED: 0,
            combinedBalanceAED: 0
          };
        }
      }

      console.log('[BrokerLive] Final broker live data map:', dataMap);
      setBrokerLiveData(dataMap);
    } catch (err) {
      console.error('[BrokerLive] Error refreshing broker live commission:', err);
    } finally {
      setLiveLoading(false);
    }
  };

  const chartData = useMemo(() => {
    const byDate = new Map();
    timeseries.forEach((entry) => {
      const dateStr = new Date(entry.date).toISOString().split('T')[0];
      const current = byDate.get(dateStr) || { date: dateStr, totalDailyCommission: 0 };
      current.totalDailyCommission += entry.totalDailyCommission || 0;
      byDate.set(dateStr, current);
    });

    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [timeseries]);

  const fetchBrokers = async () => {
    try {
      const response = await api.get('/api/brokers');
      setBrokers(response.data.brokers);
    } catch (error) {
      console.error('Error fetching brokers:', error);
      showError('Failed to fetch brokers');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    if (useCustomRange && customRange.from && customRange.to) {
      return { startDate: new Date(customRange.from), endDate: new Date(customRange.to) };
    }

    const days = Number(timeframe) || 30;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (days - 1));
    return { startDate: start, endDate: end };
  };

  const fetchTimeseries = async () => {
    try {
      setTimeseriesLoading(true);
      const { startDate, endDate } = getDateRange();
      const params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
      if (selectedBrokerIds.length && !selectedBrokerIds.includes('ALL')) {
        params.brokerIds = selectedBrokerIds.join(',');
      }
      const response = await api.get('/api/brokers/commission-timeseries', { params });
      setTimeseries(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching broker commission timeseries:', error);
      showError('Failed to load broker commission chart');
    } finally {
      setTimeseriesLoading(false);
    }
  };

  const fetchAllocatedProfits = async (brokerId) => {
    try {
      const response = await api.get(`/api/portfolio/broker/${brokerId}/allocated-profits`, {
        params: { period: allocatedProfitsPeriod }
      });
      setBrokerAllocatedProfits(response.data.data);
    } catch (error) {
      console.error('Error fetching allocated profits:', error);
      // Silently fail - this is optional data
    }
  };

  const fetchBreakdown = async (brokerId) => {
    setLoadingBreakdown(true);
    try {
      const response = await api.get(`/api/brokers/${brokerId}/investor-breakdown`);
      setBreakdownData(response.data);
      setShowBreakdownModal(true);
    } catch (error) {
      console.error('Error fetching breakdown:', error);
      showError('Failed to load breakdown data');
    } finally {
      setLoadingBreakdown(false);
    }
  };

  const fetchBrokerHistory = async (brokerId, startDate = null, endDate = null) => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      
      const response = await api.get(`/api/brokers/${brokerId}/daily-commission`, { params });
      setHistoryData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching broker history:', error);
      showError('Failed to load history data');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryModal = (broker) => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    setHistoryBroker(broker);
    setHistoryFilters({ startDate: monthStart, endDate: today });
    setShowHistoryModal(true);
    fetchBrokerHistory(broker._id, monthStart, today);
  };

  const applyHistoryFilters = () => {
    if (historyBroker) {
      fetchBrokerHistory(historyBroker._id, historyFilters.startDate, historyFilters.endDate);
    }
  };

  // Generate random password that meets requirements
  const generatePassword = () => {
    const length = 12;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    const allChars = lowercase + uppercase + numbers + special;
    
    // Ensure password has at least one of each required type
    let password = '';
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Shuffle the password to avoid predictable pattern
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData({ ...formData, password: newPassword });
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      showSuccess('Password copied to clipboard');
    } catch (error) {
      showError('Failed to copy password');
    }
  };

  const handleOpenModal = (broker = null) => {
    if (broker) {
      setEditingBroker(broker);
      setFormData({
        name: broker.name,
        email: broker.email,
        password: '',
        active: broker.active,
        startDate: broker.startDate ? (() => {
          const dateStr = broker.startDate;
          // Parse as local date to avoid timezone issues
          const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            const year = parseInt(dateMatch[1], 10);
            const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
            const day = parseInt(dateMatch[3], 10);
            return new Date(year, month, day);
          }
          return new Date(dateStr);
        })() : null
      });
    } else {
      setEditingBroker(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        active: true,
        startDate: null
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBroker(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate password meets requirements (only for new brokers or if password is provided)
      if (!editingBroker) {
        // For new brokers, password is required
        if (!formData.password || formData.password.length < 8) {
          showError('Password must be at least 8 characters long');
          return;
        }
        if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          showError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
          return;
        }
      } else if (formData.password && formData.password.trim() !== '') {
        // For editing, only validate if password is provided
        if (formData.password.length < 8) {
          showError('Password must be at least 8 characters long');
          return;
        }
        if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          showError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
          return;
        }
      }

      // Sanitize form data before submission
      const sanitizedData = sanitizeFormData(formData);
      
      // Convert startDate to YYYY-MM-DD format to avoid timezone issues
      if (sanitizedData.startDate) {
        if (sanitizedData.startDate instanceof Date) {
          const year = sanitizedData.startDate.getFullYear();
          const month = String(sanitizedData.startDate.getMonth() + 1).padStart(2, '0');
          const day = String(sanitizedData.startDate.getDate()).padStart(2, '0');
          sanitizedData.startDate = `${year}-${month}-${day}`;
        } else if (typeof sanitizedData.startDate === 'string') {
          // If it's already a string, use it directly (should be YYYY-MM-DD)
          sanitizedData.startDate = sanitizedData.startDate;
        }
      } else {
        sanitizedData.startDate = null;
      }
      
      // Remove password from data if editing and password is empty
      if (editingBroker && (!sanitizedData.password || sanitizedData.password.trim() === '')) {
        delete sanitizedData.password;
      }
      
      if (editingBroker) {
        await api.put(`/api/brokers/${editingBroker._id}`, sanitizedData);
        showSuccess('Broker updated successfully');
      } else {
        await api.post('/api/brokers', sanitizedData);
        showSuccess('Broker created successfully');
      }
      fetchBrokers();
      handleCloseModal();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to save broker';
      showError(errorMessage);
    }
  };

  const handleDelete = async (brokerId) => {
    const confirmed = await confirm({
      title: 'Delete Broker',
      message: 'Are you sure you want to delete this broker? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/brokers/${brokerId}`);
      showSuccess('Broker deleted successfully');
      fetchBrokers();
    } catch (error) {
      showError('Failed to delete broker');
    }
  };

  const handleBulkDelete = async () => {
    if (checkedBrokers.size === 0) {
      showError('Please select at least one broker');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Multiple Brokers',
      message: `Are you sure you want to delete ${checkedBrokers.size} broker(s)? This action cannot be undone.`,
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const deletePromises = Array.from(checkedBrokers).map(id => api.delete(`/api/brokers/${id}`));
      await Promise.all(deletePromises);
      showSuccess(`${checkedBrokers.size} broker(s) deleted successfully`);
      setCheckedBrokers(new Set());
      fetchBrokers();
    } catch (error) {
      showError('Failed to delete one or more brokers');
    }
  };

  const toggleBrokerCheck = (brokerId) => {
    const newSet = new Set(checkedBrokers);
    if (newSet.has(brokerId)) {
      newSet.delete(brokerId);
    } else {
      newSet.add(brokerId);
    }
    setCheckedBrokers(newSet);
  };

  const toggleAllBrokersCheck = () => {
    if (checkedBrokers.size === brokers.length) {
      setCheckedBrokers(new Set());
    } else {
      setCheckedBrokers(new Set(brokers.map(b => b._id)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="text-primary-600 dark:text-primary-400" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Brokers</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage brokers and their settings</p>
          </div>
        </div>
        {currentUser?.role === 'admin' && (
          <Button onClick={() => handleOpenModal()} variant="primary" leftIcon={<Plus size={20} />}>
            Add Broker
          </Button>
        )}
      </div>

      {/* Live Broker Commission Data (Minute-Level) */}
      <Card className="p-6 border-2 border-success-200 dark:border-success-800 bg-success-50/30 dark:bg-success-900/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Activity className="text-success-600 dark:text-success-400 animate-pulse" size={28} />
            <div>
              <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-50">Live Commission Data (Minute-Level)</h2>
              <p className="text-sm text-primary-600 dark:text-primary-400">Real-time profit tracking updated every 30 seconds</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-primary-700 dark:text-primary-300">
              <input
                type="checkbox"
                checked={autoRefreshLive}
                onChange={(e) => setAutoRefreshLive(e.target.checked)}
                className="w-4 h-4 text-success-600 bg-white dark:bg-primary-800 border-primary-300 dark:border-primary-600 rounded focus:ring-success-500"
              />
              Auto-refresh
            </label>
            <Button size="sm" variant="outline" onClick={refreshAllBrokerLive} leftIcon={<RefreshCw size={16} />}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* AVG ROI (NOW) */}
          <div className="bg-white dark:bg-primary-800 rounded-xl p-6 shadow-sm border border-primary-200 dark:border-primary-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">AVG ROI (NOW)</span>
              <Activity className="text-primary-600 dark:text-primary-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-primary-900 dark:text-primary-50">
              {liveLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  {Object.values(brokerLiveData).length > 0
                    ? (Object.values(brokerLiveData).reduce((sum, data) => sum + (data.avgROI || 0), 0) / Object.values(brokerLiveData).length).toFixed(2)
                    : '0.00'}%
                </>
              )}
            </div>
          </div>

          {/* PROFIT/MIN */}
          <div className="bg-white dark:bg-primary-800 rounded-xl p-6 shadow-sm border border-primary-200 dark:border-primary-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-success-600 dark:text-success-400">PROFIT/MIN</span>
              <TrendingUp className="text-success-600 dark:text-success-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-success-600 dark:text-success-400 flex items-center gap-2">
              {liveLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <DirhamSymbol size={24} inline />
                  {Object.values(brokerLiveData).reduce((sum, data) => sum + (data.perMinAED || 0), 0).toFixed(2)}
                </>
              )}
            </div>
          </div>

          {/* PROFIT/HOUR */}
          <div className="bg-white dark:bg-primary-800 rounded-xl p-6 shadow-sm border border-primary-200 dark:border-primary-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-info-600 dark:text-info-400">PROFIT/HOUR</span>
              <Zap className="text-info-600 dark:text-info-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-info-600 dark:text-info-400 flex items-center gap-2">
              {liveLoading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <DirhamSymbol size={24} inline />
                  {(Object.values(brokerLiveData).reduce((sum, data) => sum + (data.perMinAED || 0), 0) * 60).toFixed(2)}
                </>
              )}
            </div>
          </div>

          {/* TRACKING */}
          <div className="bg-white dark:bg-primary-800 rounded-xl p-6 shadow-sm border border-primary-200 dark:border-primary-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">TRACKING</span>
              <Users className="text-purple-600 dark:text-purple-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {brokers.filter(b => b.active).length}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-50">Broker Commission Trend</h2>
              <p className="text-sm text-primary-600 dark:text-primary-400">Daily commission (sum of selected brokers)</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {[7, 14, 30, 60, 90].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={timeframe === String(d) && !useCustomRange ? 'primary' : 'outline'}
                  onClick={() => {
                    setUseCustomRange(false);
                    setTimeframe(String(d));
                  }}
                >
                  Last {d} days
                </Button>
              ))}
              <Button
                size="sm"
                variant={useCustomRange ? 'primary' : 'outline'}
                onClick={() => setUseCustomRange(!useCustomRange)}
                leftIcon={<Calendar size={14} />}
              >
                Custom Range
              </Button>
              <Button size="sm" variant="ghost" onClick={fetchTimeseries} leftIcon={<RefreshCw size={14} />}>
                Refresh
              </Button>
              <label className="flex items-center gap-2 ml-2 text-sm">
                <input type="checkbox" checked={autoRefreshLive} onChange={(e) => setAutoRefreshLive(e.target.checked)} />
                <span>Auto-refresh live</span>
              </label>
              <Button size="sm" variant="outline" onClick={refreshAllBrokerLive} leftIcon={<RefreshCw size={14} />}>
                Refresh Live
              </Button>
            </div>
          </div>

          {useCustomRange && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-primary-600 dark:text-primary-400">From</label>
                <input
                  type="date"
                  value={customRange.from}
                  onChange={(e) => setCustomRange((prev) => ({ ...prev, from: e.target.value }))}
                  className="w-full mt-1 rounded border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-primary-600 dark:text-primary-400">To</label>
                <input
                  type="date"
                  value={customRange.to}
                  onChange={(e) => setCustomRange((prev) => ({ ...prev, to: e.target.value }))}
                  className="w-full mt-1 rounded border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="w-full max-w-xl">
            <label className="text-xs text-primary-600 dark:text-primary-400 mb-1 block">Broker</label>
            <select
              value={selectedBrokerIds[0] || ''}
              onChange={(e) => setSelectedBrokerIds(e.target.value ? [e.target.value] : [])}
              className="w-full rounded border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900 px-3 py-2 text-sm min-h-[48px]"
            >
              <option value="ALL">All brokers</option>
              {brokers.map((broker) => (
                <option key={broker._id} value={broker._id}>
                  {broker.name}
                </option>
              ))}
            </select>
          </div>

          <div className="h-80">
            {timeseriesLoading ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="lg" />
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-primary-500 dark:text-primary-300">No data for selected range</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <defs>
                    <linearGradient id="brokerCommission" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`AED ${Number(value || 0).toLocaleString()}`, 'Daily Commission']} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="totalDailyCommission"
                    name="Daily Commission"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#brokerCommission)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        {checkedBrokers.size > 0 && (
          <div className="mb-4 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700 rounded-lg flex items-center justify-between">
            <span className="text-warning-900 dark:text-warning-50 font-medium">
              {checkedBrokers.size} selected
            </span>
            <Button
              onClick={handleBulkDelete}
              variant="danger"
              size="sm"
            >
              Delete Selected
            </Button>
          </div>
        )}
        <DataTable
          columns={[
            {
              key: 'checkbox',
              label: (
                <input
                  type="checkbox"
                  checked={checkedBrokers.size === brokers.length && brokers.length > 0}
                  onChange={toggleAllBrokersCheck}
                  className="w-4 h-4"
                />
              ),
              render: (broker) => (
                <input
                  type="checkbox"
                  checked={checkedBrokers.has(broker._id)}
                  onChange={() => toggleBrokerCheck(broker._id)}
                  className="w-4 h-4"
                />
              ),
            },
            {
              key: 'name',
              label: 'Name',
              sortKey: 'name',
              exportRender: (broker) => broker.name || '',
              render: (broker) => (
                <button
                  onClick={async () => {
                    setSelectedBroker(broker);
                    setShowDrawer(true);
                    // Fetch investors for this broker
                    setLoadingInvestors(true);
                    try {
                      const response = await api.get(`/api/brokers/${broker._id}/investors`);
                      setBrokerInvestors(response.data.investors || []);
                      // Fetch allocated profits for this broker
                      await fetchAllocatedProfits(broker._id);
                    } catch (error) {
                      console.error('Error fetching broker investors:', error);
                      setBrokerInvestors([]);
                    } finally {
                      setLoadingInvestors(false);
                    }
                  }}
                  className="text-left font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline transition-colors cursor-pointer"
                >
                  {broker.name}
                </button>
              ),
            },
            { 
              key: 'email', 
              label: 'Email',
              sortKey: 'email',
              exportRender: (broker) => broker.email || '',
            },
            {
              key: 'roi_range',
              label: 'ROI Range',
              sortValue: (broker) => {
                // Calculate average ROI from BrokerInvestor relationships
                const relationships = broker.brokerInvestors || [];
                if (relationships.length > 0) {
                  const avgRoiMin = relationships.reduce((sum, rel) => sum + (rel.broker_min_roi || 0), 0) / relationships.length;
                  const avgRoiMax = relationships.reduce((sum, rel) => sum + (rel.broker_max_roi || 0), 0) / relationships.length;
                  return (avgRoiMin + avgRoiMax) / 2;
                }
                return 0;
              },
              render: (broker) => {
                // Calculate average ROI from BrokerInvestor relationships
                const relationships = broker.brokerInvestors || [];
                if (relationships.length > 0) {
                  const avgRoiMin = relationships.reduce((sum, rel) => sum + (rel.broker_min_roi || 0), 0) / relationships.length;
                  const avgRoiMax = relationships.reduce((sum, rel) => sum + (rel.broker_max_roi || 0), 0) / relationships.length;
                  return (
                    <button
                      onClick={() => fetchBreakdown(broker._id)}
                      className="text-left text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline transition-colors cursor-pointer font-medium"
                      title="Click to see per-investor breakdown"
                    >
                      {`${avgRoiMin.toFixed(1)}% - ${avgRoiMax.toFixed(1)}%`}
                      <span className="ml-1 text-xs opacity-70">(Avg)</span>
                    </button>
                  );
                }
                return 'No data';
              },
              exportRender: (broker) => {
                const minRoi = broker.min_roi ?? broker.minRoi ?? 0;
                const maxRoi = broker.max_roi ?? broker.maxRoi ?? 0;
                return `${minRoi.toFixed(1)}% - ${maxRoi.toFixed(1)}%`;
              },
            },
            {
              key: 'live_commission',
              label: 'LIVE COMMISSION (NOW)',
              sortValue: (broker) => brokerLiveData[broker._id]?.perMinAED || 0,
              render: (broker) => {
                const live = brokerLiveData[broker._id];
                if (!live) return <span className="text-primary-600 dark:text-primary-400 text-sm">--</span>;
                return (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-primary-900 dark:text-primary-50">{(live.avgROI || 0).toFixed(2)}%</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-success-600 dark:text-success-400">
                      <DirhamSymbol size={12} inline />
                      {(live.perMinAED || 0).toFixed(4)}/min
                    </div>
                    <div className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                      <DirhamSymbol size={12} inline />
                      {(live.perDayAED || 0).toFixed(2)} / day
                    </div>
                  </div>
                );
              },
              exportRender: (broker) => {
                const live = brokerLiveData[broker._id];
                if (!live) return 'No data';
                return `${(live.avgROI || 0).toFixed(2)}% | ${(live.perMinAED || 0).toFixed(4)}/min | ${(live.perDayAED || 0).toFixed(2)}/day`;
              },
            },
            {
              key: 'today_profit',
              label: "Today's Profit",
              sortValue: (broker) => (brokerLiveData[broker._id]?.todayAED || 0),
              render: (broker) => {
                const live = brokerLiveData[broker._id];
                const value = live?.todayAED || 0;
                return (
                  <span className="flex items-center gap-1 font-semibold text-primary-900 dark:text-primary-50">
                    <DirhamSymbol size={16} inline />
                    {value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                );
              },
              exportRender: (broker) => ((brokerLiveData[broker._id]?.todayAED || 0).toFixed(2)),
            },
            {
              key: 'monthly_profit',
              label: 'Monthly Profit',
              sortValue: (broker) => (brokerLiveData[broker._id]?.aggregatedMonthlyAED || 0),
              render: (broker) => {
                const live = brokerLiveData[broker._id];
                const value = live?.aggregatedMonthlyAED || 0;
                return (
                  <span className="flex items-center gap-1 text-primary-900 dark:text-primary-50">
                    <DirhamSymbol size={16} inline />
                    {value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                );
              },
              exportRender: (broker) => ((brokerLiveData[broker._id]?.aggregatedMonthlyAED || 0).toFixed(2)),
            },
            {
              key: 'balance',
              label: 'Balance',
              sortValue: (broker) => (brokerLiveData[broker._id]?.combinedBalanceAED || 0),
              render: (broker) => {
                const dailyROIBalance = brokerLiveData[broker._id]?.aggregatedBalanceAED || 0;
                const profitAllocationBalance = brokerLiveData[broker._id]?.profitAllocationAED || 0;
                const totalPayouts = brokerLiveData[broker._id]?.totalPayoutsAED || 0;
                const combinedBalance = brokerLiveData[broker._id]?.combinedBalanceAED || 0;
                
                return (
                  <button
                    onClick={() => openHistoryModal(broker)}
                    className="flex flex-col items-start hover:opacity-80 transition-opacity cursor-pointer"
                    title="Click to view full history"
                  >
                    <div className="flex items-center gap-1 font-bold text-primary-900 dark:text-primary-50">
                      <DirhamSymbol size={16} inline />
                      {combinedBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {profitAllocationBalance > 0 && (
                      <div className="text-xs text-success-600 dark:text-success-400 flex items-center gap-1">
                        <span>+</span>
                        <DirhamSymbol size={10} inline />
                        {profitAllocationBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="ml-1">from extra profit</span>
                      </div>
                    )}
                    {totalPayouts > 0 && (
                      <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                        <span>-</span>
                        <DirhamSymbol size={10} inline />
                        {totalPayouts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="ml-1">from payouts</span>
                      </div>
                    )}
                  </button>
                );
              },
              exportRender: (broker) => ((brokerLiveData[broker._id]?.combinedBalanceAED || 0).toFixed(2)),
            },
            {
              key: 'today_roi',
              label: "Today's ROI %",
              sortValue: (broker) => (brokerLiveData[broker._id]?.avgROI || 0),
              render: (broker) => {
                const rate = brokerLiveData[broker._id]?.avgROI || 0;
                return (
                  <span className="font-semibold text-primary-900 dark:text-primary-50">
                    {rate.toFixed(2)}%
                  </span>
                );
              },
              exportRender: (broker) => (((brokerLiveData[broker._id]?.avgROI || 0).toFixed(2) + '%')),
            },
            {
              key: 'investors',
              label: 'Investors',
              sortKey: 'investorCount',
              render: (broker) => {
                const investorCount = broker.investorCount || 0;
                return (
                  <Link
                    to={`/brokers/${broker._id}/investors`}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline font-medium transition-colors"
                  >
                    {investorCount}
                  </Link>
                );
              },
              exportRender: (broker) => broker.investorCount || 0,
            },
            {
              key: 'date',
              label: 'DATE',
              sortValue: (broker) => {
                const dateValue = broker.startDate || broker.createdAt || broker.created_at;
                return dateValue ? new Date(dateValue).getTime() : 0;
              },
              render: (broker) => {
                // Prioritize startDate over createdAt
                const dateValue = broker.startDate || broker.createdAt || broker.created_at;
                if (!dateValue) return 'N/A';
                try {
                  const date = new Date(dateValue);
                  if (isNaN(date.getTime())) return 'N/A';
                  return (
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-primary-600 dark:text-primary-400" />
                      <span className="text-primary-900 dark:text-primary-50">
                        {date.toISOString().split('T')[0]}
                      </span>
                    </div>
                  );
                } catch (e) {
                  return 'N/A';
                }
              },
              exportRender: (broker) => {
                const dateValue = broker.startDate || broker.createdAt || broker.created_at;
                if (!dateValue) return 'N/A';
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return 'N/A';
                return date.toISOString().split('T')[0];
              },
            },
            {
              key: 'status',
              label: 'Status',
              sortValue: (broker) => (broker.active ? 'Active' : 'Inactive'),
              render: (broker) => (
                <Badge variant={broker.active ? 'success' : 'default'}>
                  {broker.active ? 'Active' : 'Inactive'}
                </Badge>
              ),
              exportRender: (broker) => (broker.active ? 'Active' : 'Inactive'),
            },
            {
              key: 'actions',
              label: 'Actions',
              render: (broker) => (
                <div className="flex items-center justify-center gap-2">
                  {currentUser?.role === 'admin' && (
                    <>
                      <button
                        onClick={() => handleOpenModal(broker)}
                        className="p-2 text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(broker._id)}
                        className="p-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              ),
            },
          ]}
          data={brokers}
          striped
          hover
          exportFileName="brokers"
          searchableFields={['name', 'email']}
          enableColumnFilters
        />
      </Card>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingBroker ? 'Edit Broker' : 'Create Broker'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          <FormField
            label="Broker Name"
            name="name"
            type="text"
            sanitizeType="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter broker name"
          />
          <FormField
            label="Email"
            name="email"
            type="email"
            sanitizeType="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Enter email address"
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
                Password {editingBroker ? '(leave blank to keep current)' : '*'}
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGeneratePassword}
                  leftIcon={<RefreshCw size={14} />}
                >
                  Auto Generate
                </Button>
                {formData.password && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyPassword}
                    leftIcon={<Copy size={14} />}
                  >
                    Copy
                  </Button>
                )}
              </div>
            </div>
            <PasswordField
              name="password"
              required={!editingBroker}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder={editingBroker ? "Enter new password (optional)" : "Enter password for broker login"}
            />
          </div>
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={formData.startDate}
            onChange={(e) => {
              // FormDatePicker now sends YYYY-MM-DD string, parse it as local date
              if (e.target.value) {
                const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
                  const day = parseInt(dateMatch[3], 10);
                  const dateValue = new Date(year, month, day);
                  setFormData({ ...formData, startDate: dateValue });
                } else {
                  setFormData({ ...formData, startDate: null });
                }
              } else {
                setFormData({ ...formData, startDate: null });
              }
            }}
            placeholderText="Select start date"
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="w-4 h-4 rounded border-primary-300 dark:border-primary-700 text-secondary-500 focus:ring-secondary-500"
            />
            <label htmlFor="active" className="text-sm font-medium text-primary-700 dark:text-primary-300">
              Active
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingBroker ? 'Update' : 'Create'}
            </Button>
            <Button type="button" onClick={handleCloseModal} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Broker Details Drawer */}
      <Drawer
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setSelectedBroker(null);
          setBrokerInvestors([]);
        }}
        title="Broker Details"
        size="lg"
        position="right"
      >
        {selectedBroker && (
          <div className="space-y-6">
            {/* Edit Button at Top */}
            {currentUser?.role === 'admin' && (
              <div className="pb-4 border-b border-primary-200 dark:border-primary-700">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowDrawer(false);
                    handleOpenModal(selectedBroker);
                  }}
                  className="w-full"
                  leftIcon={<Edit size={18} />}
                >
                  Edit Broker
                </Button>
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Basic Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Name
                  </label>
                  <p className="mt-1 text-base text-primary-900 dark:text-primary-50">
                    {selectedBroker.name}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Email
                  </label>
                  <p className="mt-1 text-base text-primary-900 dark:text-primary-50">
                    {selectedBroker.email}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Status
                  </label>
                  <div className="mt-1">
                    <Badge variant={selectedBroker.active ? 'success' : 'default'}>
                      {selectedBroker.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* ROI Information */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                ROI Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Minimum ROI
                  </label>
                  <p className="mt-1 text-base text-primary-900 dark:text-primary-50">
                    {(selectedBroker.min_roi ?? selectedBroker.minRoi ?? 0).toFixed(1)}%
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Maximum ROI
                  </label>
                  <p className="mt-1 text-base text-primary-900 dark:text-primary-50">
                    {(selectedBroker.max_roi ?? selectedBroker.maxRoi ?? 0).toFixed(1)}%
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    ROI Range
                  </label>
                  <p className="mt-1 text-base text-primary-900 dark:text-primary-50 font-semibold">
                    {(selectedBroker.min_roi ?? selectedBroker.minRoi ?? 0).toFixed(1)}% - {(selectedBroker.max_roi ?? selectedBroker.maxRoi ?? 0).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <Divider />

            {/* Commission Information */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Commission
              </h3>
              <div>
                <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  Total Commission
                </label>
                <p className="mt-1 text-2xl font-bold text-primary-900 dark:text-primary-50 flex items-center gap-2">
                  <DirhamSymbol size={24} inline />
                  {((selectedBroker.total_commission_aed ?? selectedBroker.totalCommissionAed ?? 0)).toLocaleString('en-US')}
                </p>
              </div>
            </div>

            <Divider />

            {/* Investors Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide">
                  Investors ({brokerInvestors.length})
                </h3>
                {brokerInvestors.length > 0 && (
                  <Link
                    to={`/brokers/${selectedBroker._id}/investors`}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 font-medium"
                  >
                    View All →
                  </Link>
                )}
              </div>
              {loadingInvestors ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="sm" />
                </div>
              ) : brokerInvestors.length > 0 ? (
                <div className="space-y-3">
                  {brokerInvestors.slice(0, 5).map((rel) => (
                    <div
                      key={rel._id}
                      className="p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg border border-primary-200 dark:border-primary-700"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-primary-900 dark:text-primary-50 text-sm">
                            {rel.investor_id?.firstName && rel.investor_id?.lastName
                              ? `${rel.investor_id.firstName} ${rel.investor_id.lastName}`
                              : rel.investor_id?.email || 'Unknown Investor'}
                          </p>
                          <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                            {rel.investor_id?.email}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-xs font-medium text-primary-600 dark:text-primary-400">
                            ROI Range
                          </p>
                          <p className="text-sm font-semibold text-primary-900 dark:text-primary-50">
                            {(rel.roi_min ?? 0).toFixed(1)}% - {(rel.roi_max ?? 0).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-primary-200 dark:border-primary-700">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary-600 dark:text-primary-400">
                            Invested Amount:
                          </span>
                          <span className="font-medium text-primary-900 dark:text-primary-50">
                            ${(rel.investor_id?.invested_amount ?? 0).toLocaleString()} USDT
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {brokerInvestors.length > 5 && (
                    <div className="pt-2">
                      <Link
                        to={`/brokers/${selectedBroker._id}/investors`}
                        className="block text-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 font-medium py-2"
                      >
                        View {brokerInvestors.length - 5} more investors →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg">
                  <Users size={32} className="mx-auto text-primary-400 dark:text-primary-600 mb-2" />
                  <p className="text-sm text-primary-600 dark:text-primary-400">
                    No investors assigned
                  </p>
                  {currentUser?.role === 'admin' && (
                    <Link
                      to={`/brokers/${selectedBroker._id}/investors`}
                      className="inline-block mt-3 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 font-medium"
                    >
                      Add Investors →
                    </Link>
                  )}
                </div>
              )}
            </div>

            <Divider />

            {/* Allocated Profits Section */}
            {brokerAllocatedProfits && (
              brokerAllocatedProfits.direct_allocations?.length > 0 ||
              brokerAllocatedProfits.commission_distributed?.length > 0
            ) && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide flex items-center gap-2">
                      <DollarSign size={18} />
                      Allocated Profits
                    </h3>
                    <select
                      value={allocatedProfitsPeriod}
                      onChange={(e) => {
                        setAllocatedProfitsPeriod(e.target.value);
                        if (selectedBroker) {
                          fetchAllocatedProfits(selectedBroker._id);
                        }
                      }}
                      className="px-3 py-1 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="7">Last 7 days</option>
                      <option value="30">Last 30 days</option>
                      <option value="60">Last 60 days</option>
                      <option value="90">Last 90 days</option>
                    </select>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <Card className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-700">
                      <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">Direct Allocated</p>
                      <p className="text-lg font-bold text-success-900 dark:text-success-50 mt-1 flex items-center gap-1">
                        <DirhamSymbol size={14} inline />
                        {(brokerAllocatedProfits.summary?.total_direct_allocated || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </p>
                    </Card>
                    <Card className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">Distributed to Investors</p>
                      <p className="text-lg font-bold text-primary-900 dark:text-primary-50 mt-1 flex items-center gap-1">
                        <DirhamSymbol size={14} inline />
                        {(brokerAllocatedProfits.summary?.total_distributed_aed || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </p>
                    </Card>
                  </div>

                  {/* Direct Allocations */}
                  {brokerAllocatedProfits.direct_allocations?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase mb-2">Direct Transaction Allocations</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {brokerAllocatedProfits.direct_allocations.map((alloc, idx) => (
                          <div key={idx} className="p-2 bg-success-50 dark:bg-success-900/20 rounded border border-success-200 dark:border-success-800 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-success-900 dark:text-success-100 font-semibold">{alloc.transaction_type}</span>
                              <span className="text-success-700 dark:text-success-300 font-bold flex items-center gap-1">
                                <DirhamSymbol size={12} inline />
                                {alloc.allocated_amount_aed?.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <p className="text-primary-600 dark:text-primary-400 mt-1">
                              {new Date(alloc.allocation_date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Commission Distributed to Investors */}
                  {brokerAllocatedProfits.commission_distributed?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase mb-2">Commission Distributed</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {brokerAllocatedProfits.commission_distributed.map((comm, idx) => (
                          <div key={idx} className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800 text-xs">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-blue-900 dark:text-blue-100 font-semibold block">{comm.investor_name}</span>
                                <span className="text-blue-700 dark:text-blue-300 text-xs">{comm.investor_email}</span>
                              </div>
                              <span className="text-blue-700 dark:text-blue-300 font-bold flex items-center gap-1">
                                <DirhamSymbol size={12} inline />
                                {comm.amount_aed?.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <p className="text-primary-600 dark:text-primary-400 mt-1">
                              {new Date(comm.date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Divider />
              </>
            )}

            {/* Timestamps */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Timestamps
              </h3>
              <div className="space-y-4">
                {selectedBroker.createdAt && (
                  <div>
                    <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      Created At
                    </label>
                    <p className="mt-1 text-base text-primary-900 dark:text-primary-50">
                      {new Date(selectedBroker.createdAt).toLocaleString()}
                    </p>
                  </div>
                )}
                {selectedBroker.updatedAt && (
                  <div>
                    <label className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      Last Updated
                    </label>
                    <p className="mt-1 text-base text-primary-900 dark:text-primary-50">
                      {new Date(selectedBroker.updatedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>

          {/* Per-Investor Breakdown Modal */}
          <Modal
            isOpen={showBreakdownModal}
            onClose={() => {
              setShowBreakdownModal(false);
              setBreakdownData(null);
            }}
            title="Commission Breakdown by Investor"
            size="xl"
          >
            {loadingBreakdown ? (
              <div className="flex justify-center items-center py-8">
                <Spinner size="lg" />
              </div>
            ) : breakdownData ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="p-4 bg-primary-50 dark:bg-primary-900/20">
                    <div className="text-sm text-primary-600 dark:text-primary-400 mb-1">Total Investors</div>
                    <div className="text-2xl font-bold text-primary-900 dark:text-primary-50">
                      {breakdownData.investor_count}
                    </div>
                  </Card>
                  <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                    <div className="text-sm text-success-600 dark:text-success-400 mb-1">Today's Commission</div>
                    <div className="text-2xl font-bold text-success-900 dark:text-success-50 flex items-center gap-1">
                      <DirhamSymbol size={20} inline />
                      {breakdownData.totals.today_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </Card>
                  <Card className="p-4 bg-warning-50 dark:bg-warning-900/20">
                    <div className="text-sm text-warning-600 dark:text-warning-400 mb-1">Monthly Commission</div>
                    <div className="text-2xl font-bold text-warning-900 dark:text-warning-50 flex items-center gap-1">
                      <DirhamSymbol size={20} inline />
                      {breakdownData.totals.monthly_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </Card>
                  <Card className="p-4 bg-info-50 dark:bg-info-900/20">
                    <div className="text-sm text-info-600 dark:text-info-400 mb-1">Average ROI</div>
                    <div className="text-2xl font-bold text-info-900 dark:text-info-50">
                      {breakdownData.totals.average_roi.toFixed(2)}%
                    </div>
                  </Card>
                </div>

                {/* Breakdown Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-primary-200 dark:divide-primary-700">
                    <thead className="bg-primary-50 dark:bg-primary-900/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          Investor
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          ROI Range
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          Invested
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          Balance
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          Today's Commission
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          Monthly Commission
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                          Total Commission
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-primary-800 divide-y divide-primary-200 dark:divide-primary-700">
                      {breakdownData.breakdown.map((item, index) => (
                        <tr key={item.investor_id} className={index % 2 === 0 ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-primary-900 dark:text-primary-50">{item.investor_name}</div>
                            <div className="text-xs text-primary-600 dark:text-primary-400">{item.investor_email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                            <div className="font-medium">{item.roi_min.toFixed(1)}% - {item.roi_max.toFixed(1)}%</div>
                            <div className="text-xs text-primary-600 dark:text-primary-400">Avg: {item.roi_average.toFixed(2)}%</div>
                            {item.broker_percentage_used_in_records && item.broker_percentage_used_in_records !== item.roi_average && (
                              <div className="text-xs text-warning-600 dark:text-warning-400">Used: {item.broker_percentage_used_in_records.toFixed(2)}%</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-xs">$</span>
                              {item.invested_usdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-xs">$</span>
                              {item.balance_usdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                            <div className="flex items-center justify-end gap-1">
                              <DirhamSymbol size={14} inline />
                              {item.today_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                            <div className="flex items-center justify-end gap-1">
                              <DirhamSymbol size={14} inline />
                              {item.monthly_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-primary-900 dark:text-primary-50">
                            <div className="flex items-center justify-end gap-1">
                              <DirhamSymbol size={14} inline />
                              {item.total_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-primary-100 dark:bg-primary-900/50 font-bold">
                        <td colSpan="4" className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50 text-right">
                          TOTALS
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          <div className="flex items-center justify-end gap-1">
                            <DirhamSymbol size={14} inline />
                            {breakdownData.totals.today_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          <div className="flex items-center justify-end gap-1">
                            <DirhamSymbol size={14} inline />
                            {breakdownData.totals.monthly_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          <div className="flex items-center justify-end gap-1">
                            <DirhamSymbol size={14} inline />
                            {breakdownData.totals.total_commission_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center text-primary-600 dark:text-primary-400 py-8">
                No breakdown data available
              </div>
            )}  </Modal>

      {/* History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setHistoryData([]);
          setHistoryBroker(null);
        }}
        title={`Commission History - ${historyBroker?.name}`}
        size="xl"
      >
        <div className="space-y-4">
          {/* Date Filters */}
          <div className="flex flex-wrap gap-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                Start Date
              </label>
              <FormDatePicker
                value={historyFilters.startDate}
                onChange={(date) => setHistoryFilters({ ...historyFilters, startDate: date })}
                placeholder="Select start date"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                End Date
              </label>
              <FormDatePicker
                value={historyFilters.endDate}
                onChange={(date) => setHistoryFilters({ ...historyFilters, endDate: date })}
                placeholder="Select end date"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={applyHistoryFilters}
                variant="primary"
                size="md"
              >
                <Filter size={16} className="mr-2" />
                Apply Filter
              </Button>
            </div>
          </div>

          {/* History Table */}
          {historyLoading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : historyData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-200 dark:divide-primary-700">
                <thead className="bg-primary-50 dark:bg-primary-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Avg ROI %
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Investors
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-primary-800 divide-y divide-primary-200 dark:divide-primary-700">
                  {historyData.map((record, index) => (
                    <tr key={`${record.date}-${record.source}-${index}`} className={index % 2 === 0 ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}>
                      <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                        {new Date(record.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                        {record.source === 'profit_allocation' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300">
                            Extra Profit
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                            Daily Commission
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                        <div className="flex items-center justify-end gap-1">
                          <DirhamSymbol size={14} inline />
                          {(record.totalDailyCommission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                        {record.source === 'profit_allocation' ? '-' : `${(record.avgBrokerPercentage || 0).toFixed(2)}%`}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                        {record.source === 'profit_allocation' ? '-' : (record.investorCount || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-primary-600 dark:text-primary-400 py-12">
              No history data available for the selected period
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Brokers;
